import { useState, useMemo, useCallback, useEffect } from "react";

import { get, merge, startCase } from "lodash";

const DEFAULT_CONFIG = {
  types: {
    post: {
      plural: 'posts',
      icon: 'file-alt',
    },
    page: {
      plural: 'pages',
      icon: 'file',
    },
  },
}

export const hookGlobalSiteSettingFactory = collectionURL => ({ fetch, siteSelected: { id: siteId } }) => {
  const [item, setItem] = useState(null)

  const fetchBySite = useCallback(async () => await fetch({
    method: 'get',
    url: `${collectionURL}/${siteId}`,
  }), [fetch, siteId])

  const fetchAndUpdate = useCallback(async () => {
    const data = await fetchBySite()
    setItem(data)
    return data
  }, [fetchBySite])

  useEffect(() => {
    fetchAndUpdate()
  }, [fetchAndUpdate])

  const hasSweeps = useCallback(() => {
    if (!item) return null
    const sweep = get(item, "theme_settings.types.sweep")
    return sweep !== undefined
  }, [item])

  const getSystemConfig = useCallback(path => {
    if (!item) return null
    const { system_config } = item
    return get(system_config, path)
  }, [item])

  const getThemeConfig = useCallback(path => {
    if (!item) return null
    const themeSettings = merge({}, DEFAULT_CONFIG, item.theme_settings)
    return get(themeSettings, path)
  }, [item])

  return useMemo(() => ({
    siteSettings: {
      item,
      fetchAndUpdate,
      hasSweeps,
      getSystemConfig,
      getThemeConfig,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      useThemeConfig: path => useMemo(() => getThemeConfig(path), [path, getThemeConfig]), // need put getThemeConfig as dependecy to recall the useMemo
      // eslint-disable-next-line react-hooks/exhaustive-deps
      useSystemConfig: path => useMemo(() => getSystemConfig(path), [path, getSystemConfig]), // need put getThemeConfig as dependecy to recall the useMemo
    },
  }), [item, fetchAndUpdate, hasSweeps, getSystemConfig, getThemeConfig])
}

export const hookGlobalGetTemplates = collectionURL => ({ fetch, fetchCms, siteSelected: { id: siteId } }) => {
  const fetchBySite = useCallback(() => fetch({
    method: 'get',
    url: `${collectionURL}/${siteId}`,
  }), [fetch, siteId])

  return useMemo(() => ({
    templates: {
      getTemplates: (folder = '', defaultOption = [{ value: null, label: 'Default' }]) => async contentType => {
        const extend = templates => defaultOption.concat(templates.map(value => ({ value, label: startCase(value) })))
        try {
          const site = await fetchBySite()
          const { theme = 'default' } = site || {}
          const { templates } = await fetchCms({
            url: `themes/${encodeURIComponent(`${theme}${folder}`)}/${contentType}`,
          })

          const options = extend(templates)

          return { options }
        } catch (err) {
          return { options: extend([]) }
        }
      },
    },
  }), [fetchCms, fetchBySite])
}
